<template>
  <!-- CI colours for "webmag Editor v3" groups (feedback 25.09.2026): brand
       colours in one list, the page's base tones automatic unless set (folded
       away; text and muted text then follow the surface with a hint of the
       brand hue, see autoGrundtoene), a live preview. The parent owns the data;
       this view only emits what changed. -->
  <div class="ci-farben-v25" data-test="ci-farben-v25">
    <b-row class="mb-4">
      <b-col cols="12" offset-sm="1" sm="3">
        <label>{{ $t('settings.ciFarben.marken') }}</label>
        <b-form-text>{{ $t('settings.ciFarben.markenDesc') }}</b-form-text>
      </b-col>
      <b-col cols="12" sm="7">
        <template v-if="teil !== 'details'">
        <div v-for="m in hauptfarben" :key="m.feld" class="ci-farbe" :data-test="`ci-marke-${m.feld}`">
          <div class="d-flex align-items-center">
            <color-picker :key="`${m.feld}-${m.wert}`" :color="m.wert" @update-color="$emit('farbe', m.index, $event)" />
            <label class="pl-3 mb-0">{{ $t(`settings.ciFarben.${m.feld}`) }}</label>
          </div>
          <b-form-text>{{ $t(`settings.ciFarben.${m.feld}Desc`) }}</b-form-text>
        </div>
        </template>

        <template v-if="teil !== 'marke'">
        <label class="mb-0 mt-2">{{ $t('settings.ciFarben.weitere') }}</label>
        <b-form-text class="pb-2">{{ $t('settings.ciFarben.weitereDesc') }}</b-form-text>
        <div
          v-for="(farbe, i) in weitere"
          :key="`weitere-${i}-${farbe}`"
          class="ci-farbe"
          data-test="ci-weitere"
        >
          <div class="d-flex align-items-center">
            <color-picker :color="farbe" @update-color="weitereSetzen(i, $event)" />
            <label class="pl-3 mb-0">{{ $t('settings.ciFarben.zusatz', { n: i + 1 }) }}</label>
            <b-button variant="link" size="sm" class="ml-2 text-danger" @click="weitereLoeschen(i)">
              {{ $t('settings.ciFarben.entfernen') }}
            </b-button>
          </div>
        </div>
        <b-button variant="outline-primary" size="sm" data-test="ci-weitere-neu" @click="$emit('weitere', [...weitere, '#000000'])">
          {{ $t('settings.ciFarben.hinzufuegen') }}
        </b-button>
        </template>
      </b-col>
    </b-row>

    <b-row v-if="teil !== 'marke'" class="mb-4">
      <b-col cols="12" offset-sm="1" sm="3">
        <label>{{ $t('settings.ciFarben.grundtoene') }}</label>
        <b-form-text>{{ $t('settings.ciFarben.grundtoeneDesc') }}</b-form-text>
      </b-col>
      <b-col cols="12" sm="7">
               <div data-test="ci-grundtoene">
          <div v-for="k in GRUNDTOENE" :key="k" class="ci-farbe" :data-test="`ci-grundton-${k}`">
            <div class="d-flex align-items-center">
              <color-picker :key="`${k}-${grundton(k)}`" :color="grundton(k)" @update-color="$emit('grundton', k, $event)" />
              <label class="pl-3 mb-0">{{ $t(`settings.ciFarben.${k}`) }}</label>
              <span v-if="!grundton(k)" class="ci-auto ml-2">{{ $t('settings.ciFarben.automatisch', { farbe: wirksam[k] }) }}</span>
              <b-button
                v-else
                variant="link"
                size="sm"
                class="ml-2"
                :data-test="`ci-grundton-auto-${k}`"
                @click="$emit('grundton', k, null)"
              >{{ $t('settings.ciFarben.aufAutomatisch') }}</b-button>
            </div>
            <b-form-text>{{ $t(`settings.ciFarben.${k}Desc`) }}</b-form-text>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="teil === 'alles'" class="mb-4">
      <b-col cols="12" offset-sm="1" sm="3">
        <label>{{ $t('settings.ciFarben.vorschau') }}</label>
      </b-col>
      <b-col cols="12" sm="7">
        <div class="ci-vorschau" :style="vorschau" data-test="ci-vorschau">
          <div class="ci-vorschau-kicker">{{ $t('settings.ciFarben.vKicker') }}</div>
          <div class="ci-vorschau-karte">
            <div class="ci-vorschau-titel">{{ $t('settings.ciFarben.vTitel') }}</div>
            <p>{{ $t('settings.ciFarben.vText') }}</p>
            <p class="ci-vorschau-leise">{{ $t('settings.ciFarben.vLeise') }}</p>
            <span class="ci-vorschau-knopf">{{ $t('settings.ciFarben.vKnopf') }}</span>
            <span class="ci-vorschau-etikett">{{ $t('settings.ciFarben.vEtikett') }}</span>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  GRUNDTOENE, dritteVierteInListe, vorschauStil, wirksameFarben,
} from './ciFarbenV25';

export default {
  name: 'CiFarbenV25',
  components: {
    ColorPicker: () => import('@/components/helper/ColorPicker.vue'),
  },
  props: {
    // corporate_identity: _customColor, _additionalColors, designTokens.colors.
    ci: { type: Object, required: true },
    // Brand kit (spec 2026-09-30): 'marke' = the two brand colours, 'details' =
    // further colours + base tones; both without the own preview. 'alles' = today.
    teil: { type: String, default: 'alles' },
  },
  data: () => ({ GRUNDTOENE }),
  computed: {
    hauptfarben() {
      const eigene = this.ci._customColor || [];
      return [
        { feld: 'primary', index: 0, wert: eigene[0] || '' },
        { feld: 'secondary', index: 1, wert: eigene[1] || '' },
      ];
    },
    weitere() {
      return Array.isArray(this.ci._additionalColors) ? this.ci._additionalColors : [];
    },
    /** What "Automatisch" currently comes to, shown next to it. */
    wirksam() {
      return wirksameFarben(this.ci);
    },
    vorschau() {
      return vorschauStil(this.ci);
    },
  },
  created() {
    // The 'details' part runs next to a 'marke' part that already did this.
    if (this.teil === 'details') return;
    // Third and fourth colour join the list (kept at its end, so Bereich tokens keep their colour).
    const umzug = dritteVierteInListe(this.ci);
    if (umzug) this.$emit('umzug', umzug);
    // "Automatisch" means derived base tones from now on (published with the next save).
    if (!(this.ci.designTokens && this.ci.designTokens.autoFarben === true)) this.$emit('auto');
  },
  methods: {
    grundton(k) {
      const t = this.ci.designTokens && this.ci.designTokens.colors;
      return (t && t[k]) || '';
    },
    weitereSetzen(i, farbe) {
      const neu = [...this.weitere];
      neu[i] = farbe;
      this.$emit('weitere', neu);
    },
    weitereLoeschen(i) {
      this.$emit('weitere', this.weitere.filter((_, j) => j !== i));
    },
  },
};
</script>

<style lang="scss" scoped>
.ci-farbe { margin-bottom: 14px; }
.ci-auto { font-size: 12px; color: #6b7280; }
.ci-vorschau {
  padding: 16px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: var(--v-page); color: var(--v-page-text);
}
.ci-vorschau-kicker { margin-bottom: 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.ci-vorschau-karte { padding: 16px; border-radius: 8px; background: var(--v-surface); color: var(--v-ink); box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); }
.ci-vorschau-titel { margin-bottom: 6px; font-size: 18px; font-weight: 700; }
.ci-vorschau-karte p { margin: 0 0 8px; font-size: 14px; }
.ci-vorschau-leise { color: var(--v-muted); font-size: 13px; }
.ci-vorschau-knopf {
  display: inline-block; padding: 6px 14px; border-radius: 6px; font-size: 13px; font-weight: 600;
  background: var(--v-primary); color: var(--v-primary-text);
}
.ci-vorschau-etikett { margin-left: 10px; font-size: 13px; font-weight: 600; color: var(--v-secondary); }
</style>
